import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { Mail, Lock, Eye, EyeOff, User, Building2, ArrowRight, LoaderCircle, ShieldCheck, MapPin, Ruler } from "lucide-react";

/* Field with a leading icon and an optional trailing action (password reveal). */
function Field({ icon: Icon, label, action, ...props }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-ink-600 tracking-wide">{label}</label>
      <div className="relative">
        <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
        <input
          {...props}
          className="w-full bg-white border border-ink-300 rounded-xl pl-10 pr-11 py-3 text-sm text-ink-900 placeholder-ink-400
                     hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all"
        />
        {action}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password, "admin");
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(regForm);
      toast.success("Account created!");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const pwToggle = (
    <button type="button" onClick={() => setShowPw(v => !v)} tabIndex={-1}
      aria-label={showPw ? "Hide password" : "Show password"}
      className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-lg text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition-all">
      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
    </button>
  );

  return (
    <div className="min-h-[100dvh] lg:grid lg:grid-cols-[1.1fr_1fr] bg-white">

      {/* ══ Brand panel — desktop only ══ */}
      <div className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-ink-900 p-12 text-white">
        {/* depth: soft brand glows + a faint survey grid */}
        <div aria-hidden className="absolute inset-0">
          <div className="absolute -top-24 -left-16 w-[28rem] h-[28rem] rounded-full bg-brand-600/30 blur-3xl" />
          <div className="absolute bottom-[-8rem] right-[-6rem] w-[26rem] h-[26rem] rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "48px 48px" }} />
        </div>

        <div className="relative flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-white/10 backdrop-blur border border-white/15 grid place-items-center">
            <span className="font-display font-bold text-lg">H</span>
          </span>
          <div>
            <div className="font-display font-bold text-lg leading-tight">HILAND Surveyors</div>
            <div className="text-white/50 text-xs">Field Operations Platform</div>
          </div>
        </div>

        <div className="relative max-w-md">
          <h1 className="font-display text-4xl font-bold leading-[1.15] tracking-tight">
            Every site, crew and rupee — in one place.
          </h1>
          <p className="text-white/60 mt-4 text-[15px] leading-relaxed">
            Track survey sites, approve field expenses, manage drawings and raise bills without leaving the office.
          </p>

          <div className="mt-10 space-y-3">
            {[
              { icon: MapPin, text: "Live site map with GPS-pinned locations" },
              { icon: Ruler, text: "Drawing revisions shared straight to the crew" },
              { icon: ShieldCheck, text: "Expense approvals with a full audit trail" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm text-white/75">
                <span className="w-8 h-8 rounded-lg bg-white/10 border border-white/10 grid place-items-center shrink-0">
                  <Icon size={15} />
                </span>
                {text}
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-white/35 text-xs">© {new Date().getFullYear()} HILAND Surveyors. All rights reserved.</p>
      </div>

      {/* ══ Form panel ══ */}
      <div className="relative flex items-center justify-center min-h-[100dvh] lg:min-h-0 px-5 py-10 bg-[var(--app-bg)] lg:bg-white">
        {/* mobile-only ambient wash */}
        <div aria-hidden className="lg:hidden absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-brand-200/40 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-cyan-200/40 blur-3xl" />
        </div>

        <div className="relative w-full max-w-[380px] animate-fade-up">
          {/* logo — mobile shows the mark, desktop keeps it lighter */}
          <div className="text-center mb-7">
            <img src="/logo.png" alt="HILAND Surveyors" className="mx-auto h-14 sm:h-16 object-contain" />
            <h2 className="mt-5 text-2xl font-bold text-ink-900 font-display">
              {showRegister ? "Create your account" : "Welcome back"}
            </h2>
            <p className="text-ink-400 text-sm mt-1.5">
              {showRegister ? "Set up a new admin workspace" : "Sign in to the admin portal"}
            </p>
          </div>

          <div className="bg-white border border-ink-200/80 rounded-2xl shadow-card p-5 sm:p-6">
            {!showRegister ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <Field icon={Mail} label="Email address" type="email" required autoComplete="email"
                  placeholder="admin@hilandsurveyors.in" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />

                <Field icon={Lock} label="Password" type={showPw ? "text" : "password"} required autoComplete="current-password"
                  placeholder="••••••••" value={form.password} action={pwToggle}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} />

                <button type="submit" disabled={loading}
                  className="w-full h-12 mt-1 rounded-xl bg-brand-600 hover:bg-brand-700 active:scale-[.99] disabled:opacity-60 disabled:pointer-events-none
                             text-white font-semibold text-sm shadow-xs transition-all flex items-center justify-center gap-2">
                  {loading ? <><LoaderCircle size={16} className="animate-spin" /> Signing in…</> : <>Sign In <ArrowRight size={16} /></>}
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <Field icon={User} label="Full name" type="text" required placeholder="Your full name"
                  value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
                <Field icon={Mail} label="Email" type="email" required placeholder="you@hilandsurveyors.in"
                  value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
                <Field icon={Building2} label="Company" type="text" required placeholder="HILAND Surveyors"
                  value={regForm.company} onChange={(e) => setRegForm({ ...regForm, company: e.target.value })} />
                <Field icon={Lock} label="Password" type={showPw ? "text" : "password"} required minLength={6}
                  placeholder="Min. 6 characters" value={regForm.password} action={pwToggle}
                  onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />

                <button type="submit" disabled={loading}
                  className="w-full h-12 mt-1 rounded-xl bg-brand-600 hover:bg-brand-700 active:scale-[.99] disabled:opacity-60 disabled:pointer-events-none
                             text-white font-semibold text-sm shadow-xs transition-all flex items-center justify-center gap-2">
                  {loading ? <><LoaderCircle size={16} className="animate-spin" /> Creating…</> : <>Create account <ArrowRight size={16} /></>}
                </button>

                <button type="button" onClick={() => setShowRegister(false)}
                  className="w-full text-sm text-ink-500 hover:text-ink-800 py-1 transition-colors">
                  ← Back to sign in
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-ink-400 text-xs mt-6 leading-relaxed">
            Crew member? Use the <span className="font-semibold text-ink-500">HILAND Surveyors Crew</span> app.
          </p>
        </div>
      </div>
    </div>
  );
}
